/**
 * The plate a hospital's logo is drawn on.
 *
 * A PNG logo with a transparent background is drawn straight onto whatever is
 * behind it. A logo drawn in dark lines vanishes on the dark themes and on the
 * queue TV; one drawn in light lines vanishes on the light themes and on the
 * printed receipt. The administrative manager picks a plate once, beside the
 * upload, and every screen that draws the logo draws it on that plate — the
 * sidebar, the header, the receipts, the shift report, the TV and the
 * patient's pages.
 *
 * The plate is a fixed colour, never a theme colour: a white plate has to stay
 * white on the dark themes, which is the whole point of it. What the plates
 * look like is in styles/index.css (.logo-plate); the server keeps the same
 * three ids (Backend/lib/themes.js LOGO_BACKDROPS) and refuses any other.
 *
 * `readLogoInk` looks at the logo itself — how much of it is see-through, and
 * whether what is left is mostly dark or mostly light — so the panel can
 * suggest the plate, and pick it for a freshly uploaded logo, instead of
 * asking a receptionist-turned-administrator what "contrast" means.
 */

export const LOGO_BACKDROPS = Object.freeze([
  { id: 'none', name: 'بدون خلفية', hint: 'كما رُفع — للشعار الملوّن', icon: 'circle-dashed' },
  { id: 'light', name: 'خلفية فاتحة', hint: 'لشعار خطوطه داكنة', icon: 'sun' },
  { id: 'dark', name: 'خلفية داكنة', hint: 'لشعار خطوطه فاتحة', icon: 'moon' },
]);

export const LOGO_BACKDROP_IDS = Object.freeze(LOGO_BACKDROPS.map((b) => b.id));

export const isLogoBackdrop = (id) => LOGO_BACKDROP_IDS.includes(id);

/** The class that draws a logo on its plate; an unknown id draws it as uploaded. */
export const logoPlateClass = (id) => `logo-plate logo-plate--${isLogoBackdrop(id) ? id : 'none'}`;

/** Below this alpha a pixel is background, not logo. */
const SEE_THROUGH = 24;
/** At or above this alpha a pixel counts towards the logo's ink. */
const SOLID = 128;
/** Luminance (0–255) under which ink is dark, and over which it is light. */
const DARK_BELOW = 96;
const LIGHT_ABOVE = 176;
/** A logo with less see-through than this carries its own background. */
const TRANSPARENT_SHARE = 0.04;
/** Ink this dominant (share of the logo, and twice the other kind) decides the plate. */
const DOMINANT = 0.45;

/**
 * What a logo's pixels say about the plate it needs.
 *
 * @param {Uint8ClampedArray|number[]} data  RGBA bytes, as ImageData.data
 * @returns {{ transparent: boolean, ink: 'dark'|'light'|'mixed'|null, suggestion: 'none'|'light'|'dark' }}
 *   `transparent` — enough of the image is see-through for a plate to matter;
 *   `ink` — what colour the logo itself is drawn in (null: nothing solid);
 *   `suggestion` — the plate that keeps it visible on every theme.
 */
export function readLogoInk(data) {
  const pixels = Math.floor((data?.length || 0) / 4);
  if (!pixels) return { transparent: false, ink: null, suggestion: 'none' };

  let clear = 0;
  let solid = 0;
  let dark = 0;
  let light = 0;
  for (let i = 0; i < pixels * 4; i += 4) {
    const alpha = data[i + 3];
    if (alpha < SEE_THROUGH) {
      clear += 1;
      continue;
    }
    if (alpha < SOLID) continue;
    solid += 1;
    const luminance = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    if (luminance < DARK_BELOW) dark += 1;
    else if (luminance > LIGHT_ABOVE) light += 1;
  }

  const transparent = clear / pixels >= TRANSPARENT_SHARE;
  if (!solid) return { transparent, ink: null, suggestion: 'none' };

  const darkShare = dark / solid;
  const lightShare = light / solid;
  let ink = 'mixed';
  if (darkShare >= DOMINANT && darkShare >= lightShare * 2) ink = 'dark';
  else if (lightShare >= DOMINANT && lightShare >= darkShare * 2) ink = 'light';

  // An opaque logo brings its own background: a plate would only frame it.
  if (!transparent) return { transparent, ink, suggestion: 'none' };
  return { transparent, ink, suggestion: ink === 'dark' ? 'light' : ink === 'light' ? 'dark' : 'none' };
}

/** The sentence the panel shows under the choice, for what readLogoInk found. */
export function logoInkAdvice(reading) {
  if (!reading) return null;
  if (!reading.transparent) return 'شعارك له خلفية خاصة به — غالباً لا يحتاج خلفية إضافية.';
  if (reading.ink === 'dark') return 'خطوط شعارك داكنة — الخلفية الفاتحة تُبقيه واضحاً في المظاهر الداكنة.';
  if (reading.ink === 'light') return 'خطوط شعارك فاتحة — الخلفية الداكنة تُبقيه واضحاً في المظاهر الفاتحة وعلى الورق.';
  return 'شعارك ملوّن ويظهر على أغلب الخلفيات — جرّب الخيارات وانظر المعاينة.';
}

/**
 * Read a logo image in the browser. Drawn small: the verdict is about the
 * logo's colours, not its detail, and 64 × 64 is 4,096 pixels to count.
 * Resolves to null when the image cannot be read.
 */
export function inspectLogo(src, edge = 64) {
  return new Promise((resolve) => {
    if (!src || typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const image = new Image();
    image.onload = () => {
      try {
        const scale = Math.min(1, edge / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
        const width = Math.max(1, Math.round((image.naturalWidth || edge) * scale));
        const height = Math.max(1, Math.round((image.naturalHeight || edge) * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, width, height);
        resolve(readLogoInk(context.getImageData(0, 0, width, height).data));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
}
